import { FC, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { moveMarketToFirstByIndex, useRuntimeConfig } from "@finbackoffice/site-core";
import { formatAppNameText, TemplateIdsMap } from "@finbackoffice/fe-core";
import TeamIcon from "components/pages/sport/base/team-icon/TeamIcon";
import { DEFAULT_DATE_FORMAT, useDate } from "hooks";
import { Svg } from "components/base/svg/Svg";
import Outcome from "components/pages/sport/base/outcome/Outcome";
import Market from "components/pages/sport/base/market/Market";
import { PrematchTournamentGameContext } from "../../PrematchTournamentGame";
import styles from "./flex.module.sass";

const Flex: FC = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const [opened, setOpened] = useState({ handicap: false, total: false });
    const { formatDate } = useDate();

    const {
        game,
        handleFavClick,
        isFav,
        mainOdds,
        mainMarketStatus,
        columnCount: mainMarketColumnCount,
        onOutcomeClick,
        mainMarketId,
        mainMarket,
        isTwoColumns,
    } = useContext(PrematchTournamentGameContext);
    const totalMarkets = game.markets.filter((market) =>
        [
            TemplateIdsMap.TOTAL,
            TemplateIdsMap.TOTAL_INCL_EXTRA_INNINGS,
            TemplateIdsMap.TOTAL_GAMES,
            TemplateIdsMap.TOTAL_POINTS,
            TemplateIdsMap.TOTAL_INCL_OVERTIME,
        ].includes(market.template_id),
    );
    const handicapMarkets = game.markets.filter((market) =>
        [
            TemplateIdsMap.HANDICAP,
            TemplateIdsMap.HANDICAP_INCL_EXTRA_INNINGS,
            TemplateIdsMap.GAME_HANDICAP,
            TemplateIdsMap.POINT_HANDICAP,
            TemplateIdsMap.HANDICAP_INCL_OVERTIME,
        ].includes(market.template_id),
    );

    const sortedTotalMarkets = useMemo(() => {
        if (opened.total) {
            return totalMarkets;
        } else {
            if (totalMarkets.length > 1) {
                if (totalMarkets.length <= 3) {
                    return moveMarketToFirstByIndex(totalMarkets, 1);
                } else if (totalMarkets.length <= 7) {
                    return moveMarketToFirstByIndex(totalMarkets, 2);
                } else {
                    return moveMarketToFirstByIndex(totalMarkets, 3);
                }
            }
        }
        return totalMarkets;
    }, [opened.total, totalMarkets]);

    const sortedHandicapMarkets = useMemo(() => {
        if (opened.handicap) {
            return handicapMarkets;
        } else {
            if (handicapMarkets.length > 1) {
                if (handicapMarkets.length <= 3) {
                    return moveMarketToFirstByIndex(handicapMarkets, 1);
                } else if (handicapMarkets.length <= 7) {
                    return moveMarketToFirstByIndex(handicapMarkets, 2);
                } else {
                    return moveMarketToFirstByIndex(handicapMarkets, 3);
                }
            }
        }
        return handicapMarkets;
    }, [opened.handicap, handicapMarkets]);

    useEffect(() => {
        if (isTwoColumns && (opened.total || opened.handicap)) {
            setOpened({ total: true, handicap: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTwoColumns]);

    const handleHeaderClick = useCallback(
        (e: SyntheticEvent, type: "handicap" | "total") => {
            e.preventDefault();
            e.stopPropagation();

            if (isTwoColumns) {
                setOpened((state) => ({ total: !state.total, handicap: !state.handicap }));
            } else {
                setOpened((state) => ({ ...state, [type]: !state[type] }));
            }
        },
        [isTwoColumns],
    );

    return (
        <div className={styles.eventItem}>
            <div className={styles.eventItemHeader}>
                <div className={styles.eventItemHeaderLeftSide}>
                    <span className={styles.time}>
                        <Svg
                            src={`/${formatAppNameText(COMMON_SITE_CONFIGS.appName)}/mobile/${
                                isFav(game.id) ? "star" : "live-game-favorite-unstarred"
                            }.svg`}
                            wrapper="span"
                            className={classNames(styles.favorite, {
                                [styles.favoriteStarred]: isFav(game.id),
                            })}
                            onClick={handleFavClick}
                        />
                        <span className={styles.sheduled}>
                            {game.scheduled_at &&
                                formatDate(
                                    new Date(game.scheduled_at),
                                    `${DEFAULT_DATE_FORMAT} / HH:mm`,
                                )}
                        </span>
                    </span>
                    <div className={classNames(styles.teams, styles[`sportId-${game.sport_id}`])}>
                        <div>
                            <TeamIcon sportId={game.sport_id} teamId={game.home_team?.id} />
                            <span>{game.home_team?.name}</span>
                        </div>
                        <div>
                            <TeamIcon sportId={game.sport_id} teamId={game.away_team?.id} />
                            <span>{game.away_team?.name}</span>
                        </div>
                    </div>
                    {game.markets_count > 0 && (
                        <span className={styles.marketCount}>+{game.markets_count}</span>
                    )}
                </div>
                {mainOdds && (
                    <div className={styles.eventItemHeaderRightSide}>
                        <span className={styles.marketsTitle}>
                            <span>{mainMarket?.name}</span>
                        </span>
                        <div
                            className={classNames(
                                styles.mainMarket,
                                styles[`marketColumn-${mainMarketColumnCount}`],
                            )}>
                            <div className={styles.market}>
                                <Outcome
                                    outcome={mainOdds?.homeOutcome}
                                    marketStatus={mainMarketStatus}
                                    variant="mainMarket"
                                    onOutcomeClick={(outcome) =>
                                        onOutcomeClick(mainMarketId, outcome)
                                    }
                                />
                                {mainMarketColumnCount === "three_columns" && (
                                    <Outcome
                                        outcome={mainOdds?.drawOutcome}
                                        marketStatus={mainMarketStatus}
                                        variant="mainMarket"
                                        onOutcomeClick={(outcome) =>
                                            onOutcomeClick(mainMarketId, outcome)
                                        }
                                    />
                                )}
                                <Outcome
                                    outcome={mainOdds?.awayOutcome}
                                    marketStatus={mainMarketStatus}
                                    variant="mainMarket"
                                    onOutcomeClick={(outcome) =>
                                        onOutcomeClick(mainMarketId, outcome)
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {(!!sortedTotalMarkets.length || !!sortedHandicapMarkets.length) && (
                <div className={styles.container}>
                    {!!sortedTotalMarkets.length && (
                        <div className={styles.containerMarkets}>
                            <span
                                onClick={(e) =>
                                    sortedTotalMarkets.length > 1 && handleHeaderClick(e, "total")
                                }
                                className={classNames(
                                    styles.marketsTitle,
                                    opened.total && styles.opened,
                                )}>
                                <span>{sortedTotalMarkets[0].name}</span>
                                {sortedTotalMarkets.length > 1 && (
                                    <Svg
                                        src="/common/desktop/base-icons/arrow-white.svg"
                                        wrapper="span"
                                        className="svg-icon"
                                    />
                                )}
                            </span>
                            <div
                                className={classNames(
                                    opened.total && styles.openedContainer,
                                    styles.content,
                                    styles.mainMarket,
                                    styles[`marketColumn-two_columns`],
                                )}>
                                {sortedTotalMarkets.map((market) => (
                                    <Market
                                        key={market.id}
                                        market={market}
                                        onOutcomeClick={onOutcomeClick}
                                        outcomeVariant="mainMarket"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {!!sortedHandicapMarkets.length && (
                        <div className={styles.containerMarkets}>
                            <span
                                onClick={(e) =>
                                    sortedHandicapMarkets.length > 1 &&
                                    handleHeaderClick(e, "handicap")
                                }
                                className={classNames(
                                    styles.marketsTitle,
                                    opened.handicap && styles.opened,
                                )}>
                                <span>{sortedHandicapMarkets[0].name}</span>
                                {sortedHandicapMarkets.length > 1 && (
                                    <Svg
                                        src="/common/desktop/base-icons/arrow-white.svg"
                                        wrapper="span"
                                        className="svg-icon"
                                    />
                                )}
                            </span>
                            <div
                                className={classNames(
                                    opened.handicap && styles.openedContainer,
                                    styles.content,
                                    styles.mainMarket,
                                    styles[`marketColumn-two_columns`],
                                )}>
                                {sortedHandicapMarkets.map((market) => (
                                    <Market
                                        key={market.id}
                                        type="handicap"
                                        market={market}
                                        onOutcomeClick={onOutcomeClick}
                                        outcomeVariant="mainMarket"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Flex;
